import type { RangeFilterValue } from '../state/range';
import {
  makeContinuousRangeValue,
  RangeFilterStateMap,
  RangeFilterValue as RangeFilterValueSchema,
} from '../state/range';
import { extractRangeCode, isRangeParam, prefixRange } from './urlPrefixes';
import { BigDecimal, Exit, HashMap, Schema, SchemaGetter } from 'effect';

// `FiniteFromString` instead of `NumberFromString`: v4's `NumberFromString`
// accepts `"NaN"`/`"Infinity"`, which then crashes `BigDecimal.fromNumberUnsafe`.
const RangeFromString = Schema.TemplateLiteralParser([
  Schema.FiniteFromString,
  '_',
  Schema.FiniteFromString,
]).pipe(
  Schema.decodeTo(Schema.toType(RangeFilterValueSchema), {
    decode: SchemaGetter.transform((templateResult) => {
      const [min, , max] = templateResult;
      return makeContinuousRangeValue({
        min: BigDecimal.fromNumberUnsafe(min),
        max: BigDecimal.fromNumberUnsafe(max),
      });
    }),
    encode: SchemaGetter.transform(
      (range) =>
        [
          BigDecimal.toNumberUnsafe(range.min),
          '_' as const,
          BigDecimal.toNumberUnsafe(range.max),
        ] as const
    ),
  })
);

const rangeFilterStateMapToUrlRecord = (
  filterStateMap: RangeFilterStateMap
): Record<string, string> =>
  HashMap.reduce(filterStateMap, {} as Record<string, string>, (acc, rangeValue, attributeCode) => {
    const key = prefixRange(attributeCode);
    // Use the schema's encode function for consistency
    acc[key] = Schema.encodeSync(RangeFromString)(rangeValue);
    return acc;
  });

const urlRecordToRangeFilterStateMap = (urlRecord: Record<string, string>): RangeFilterStateMap => {
  let result = HashMap.empty<string, RangeFilterValue>();

  for (const [key, value] of Object.entries(urlRecord)) {
    if (!isRangeParam(key)) continue;

    const code = extractRangeCode(key);

    const rangeResult = Schema.decodeUnknownExit(RangeFromString)(value);

    if (Exit.isSuccess(rangeResult)) {
      result = HashMap.set(result, code, rangeResult.value);
    }
    // Silently ignore invalid range formats - they won't be included in the result
  }

  return result;
};

export const UrlQueryRecord = Schema.Record(Schema.String, Schema.String);

export const RangeToUrl = Schema.toType(RangeFilterStateMap).pipe(
  Schema.decodeTo(UrlQueryRecord, {
    decode: SchemaGetter.transform(rangeFilterStateMapToUrlRecord),
    encode: SchemaGetter.transform(urlRecordToRangeFilterStateMap),
  })
);
